/**
 * KpisDelMes — las cuatro cifras del mes, en el orden en que la plata se mueve.
 *
 * EL ORDEN ES EL RECORRIDO DEL DINERO, no una jerarquía de importancia:
 *
 *   recaudado  →  depositado por el PSP  →  lo que el PSP aún debe  →  a girar
 *
 * Leídas de izquierda a derecha cuentan la frase completa: entró tanto, el proveedor
 * ya puso tanto, falta tanto, y a las compañías les toca tanto. En otro orden son
 * cuatro números sueltos.
 *
 * QUÉ REEMPLAZA. Había cuatro KPIs de los cuales dos contaban DÍAS —«días por
 * cerrar», «días liquidados»— al lado de dos que contaban PLATA. Mezclar las dos
 * unidades en una fila obliga a cambiar de escala en cada tarjeta, y dejaba la
 * pregunta de caja a medias: no se veía cuánto había depositado el proveedor ni
 * cuánto seguía debiendo. Los conteos de días se fueron a `ColaDelMes`, que es donde
 * un día además se puede abrir.
 *
 * NINGUNA DE ESTAS CIFRAS SE CALCULA ACÁ. Todas vienen del `resumen` de
 * `GET /api/mes`: `payment` es el dueño de la aritmética del cierre y una segunda
 * suma en el navegador podría contradecirlo. Esto sólo decide cómo se leen.
 *
 * @see src/components/payments/yoolpay/ColaDelMes.jsx — el otro lado: qué días faltan
 * @see payment/src/services/calendario-mes.service.ts — de dónde sale cada número
 */
import React from 'react';

import { KPI } from './cierre/ds';
import { fmtClp, fmtNum } from '../../../lib/yoolpay/formato';
import { pspLabel } from '../../../lib/yoolpay/psp';

/** `1 transacción` / `377 transacciones`. */
const trx = (n) => `${fmtNum(n)} transacci${n === 1 ? 'ón' : 'ones'}`;

/** `1 día` / `29 días`. */
const dias = (n) => `${fmtNum(n)} día${n === 1 ? '' : 's'}`;

/**
 * Cómo se lee lo que el PSP todavía debe.
 *
 * CUATRO CASOS Y NO DOS, porque piden cosas distintas: no se puede saber, plata en
 * camino, todo depositado, y —el que se olvida— el proveedor depositó MÁS de lo que
 * declaró, que no es una buena noticia sino una cartola que no cuadra.
 */
function contextoPorCobrar(r) {
  if (r.por_cobrar_psp_clp == null) {
    return `no se puede calcular: ${dias(r.dias_sin_neto)} sin la comisión del proveedor`;
  }
  if (r.por_cobrar_psp_clp > 0) return 'cobrado que el PSP aún no deposita';
  if (r.por_cobrar_psp_clp < 0) return 'el PSP depositó más de lo que declaró: revisar la cartola';
  return 'el PSP depositó todo lo cobrado';
}

/** Los proveedores que cobraron, con nombre de UI. `null` si el mes no tuvo ninguno. */
export function pspsDelMes(resumen) {
  const ids = resumen?.psps ?? [];
  return ids.length === 0 ? null : ids.map(pspLabel).join(' · ');
}

/**
 * @param {object}  props
 * @param {object}  props.resumen  el `resumen` de `GET /api/mes`
 */
export default function KpisDelMes({ resumen }) {
  const psps = pspsDelMes(resumen);

  return (
    <section
      style={{
        display: 'grid',
        // CUATRO COLUMNAS FIJAS y no `auto-fit`: las cuatro cifras se leen como una
        // frase, y dejarlas reflowear a 3+1 la parte por la peor mitad.
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
        gap: 15,
      }}
    >
      <KPI
        label="Recaudado en el mes"
        value={fmtClp(resumen.cobrado_clp)}
        icon="banknote"
        accent="teal"
        context={
          resumen.pagos_divididos > 0
            ? `${trx(resumen.transacciones)} · ${fmtNum(resumen.pagos_divididos)} pagos divididos`
            // «0 pagos divididos» se lee como un dato que falta; que no haya carritos de
            // dos aseguradoras es lo normal, y así se dice.
            : `${trx(resumen.transacciones)} · ninguna dividida entre compañías`
        }
      />

      <KPI
        label="Depositado por el PSP"
        value={fmtClp(resumen.depositado_clp)}
        icon="wallet"
        accent="sky"
        // EL DENOMINADOR SON LOS DÍAS QUE COBRARON, no los del calendario: un domingo
        // sin cobros no es un día que el proveedor dejó de informar, y contarlo haría
        // que un mes perfecto se viera con nueve días faltantes.
        //
        // `fmtNum` y no interpolación directa: si tadmin va por delante de payment el
        // campo no existe todavía, y `${undefined}` imprime la palabra «undefined» en
        // una tarjeta de plata. Un guion dice lo mismo sin mentir.
        context={`${psps ? `${psps} · ` : ''}${fmtNum(resumen.dias_informados)} de `
          + `${dias(resumen.dias_con_movimiento)} con cobro informados`}
      />

      <KPI
        label="Por cobrar del PSP"
        value={fmtClp(resumen.por_cobrar_psp_clp)}
        icon="warn"
        accent="red"
        // ROJO SÓLO CUANDO HAY ALGO. Un KPI en alerta permanente deja de comunicar, y
        // un mes al día tiene este número en cero por definición. El `null` tampoco
        // alerta: no saber cuánto es un dato que falta, no una deuda confirmada.
        alert={resumen.por_cobrar_psp_clp != null && resumen.por_cobrar_psp_clp !== 0}
        context={contextoPorCobrar(resumen)}
      />

      <KPI
        label="A girar a las compañías"
        value={fmtClp(resumen.a_girar_clp)}
        icon="creditCard"
        accent="violet"
        // EL 100% DEL BRUTO: los fees no se descuentan del giro, se facturan aparte. Y
        // al lado lo que ya salió, porque el total sin eso obliga a restar a ojo contra
        // el calendario para saber qué falta.
        context={`el 100% de lo recaudado · ${fmtClp(resumen.girado_clp)} ya girado en `
          + 'giros diarios'}
      />
    </section>
  );
}
